import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { BadgeDollarSign, Loader2 } from "lucide-react";
import { http, errMsg } from "../../lib/api";
import { PriceStripPreview } from "./PriceStripPreview";

const status = (k) => ({
  scanning: `Preparing ${k.done} of ${k.total}${k.current ? ` · ${k.current}` : ""}`,
  ready: `Preview ready · ${k.done} venue(s)`,
  applied: `Applied · ${k.applied} venue(s)`,
  budget: "Stopped — AI budget used up",
  clean: "Clean",
}[k.status] || `${k.venues} venue(s) with prices in the text`);

const Row = ({ k, i, onView }) => (
  <li data-testid={`price-strip-kit-${i}`} className="flex items-center gap-3 border-t border-stone-100 py-1.5 text-sm">
    <span className="min-w-0 flex-1 truncate text-[#2B2A26]">{k.destination} <span className="text-xs text-stone-400">· {k.edition} · {k.tier} · built {(k.created_at || "").slice(0, 16).replace("T", " ")}</span></span>
    <span data-testid={`price-strip-kit-status-${i}`} className="text-xs text-stone-500">{k.status === "scanning" && <Loader2 size={11} className="mr-1 inline animate-spin" />}{status(k)}</span>
    {k.done > 0 && <button data-testid={`price-strip-view-${i}`} onClick={() => onView(k.id)} className="text-xs font-bold text-[#9A7B2C] hover:underline">Preview</button>}
  </li>
);

export const PriceStrip = () => {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const [view, setView] = useState(null);
  const [auto, setAuto] = useState(true);
  const load = useCallback(() => http.get("/price-strip").then((x) => setD(x.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const kits = d?.kits || [];
  const scanning = kits.some((k) => k.status === "scanning");
  const ready = kits.filter((k) => k.status === "ready").length;
  useEffect(() => {
    if (!scanning) return undefined;
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
  }, [scanning, load]);
  const post = async (url, msg) => {
    setBusy(true);
    try { const r = await http.post(url); toast.success(msg(r.data)); await load(); } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  if (!kits.length) return null;
  return (
    <section data-testid="price-strip-panel" className="mb-6 rounded-lg border border-stone-200 bg-white p-4">
      <div className="flex flex-wrap items-center gap-3">
        <BadgeDollarSign size={18} className="text-[#9A7B2C]" />
        <b className="text-[#2B2A26]">Prices in descriptions</b>
        <span data-testid="price-strip-summary" className="text-sm text-stone-500">{d.venues} researched venue(s) still mention a price in the text — prices should only sit under What it costs</span>
        <div className="ml-auto flex items-center gap-2">
          <label className="flex items-center gap-1.5 text-xs text-stone-600"><input data-testid="price-strip-auto-checkbox" type="checkbox" checked={auto} onChange={(e) => setAuto(e.target.checked)} />Apply as soon as ready</label>
          <button data-testid="price-strip-scan-btn" disabled={busy || scanning || !d.venues} onClick={() => post(`/price-strip/scan?auto=${auto}`, (x) => `Cleaning ${x.venues} venue(s) in ${x.kits} kit(s)${auto ? " — each kit is applied as soon as it's ready" : ""}`)}
            className="inline-flex items-center gap-1.5 rounded-sm bg-[#2B2A26] px-3 py-1.5 text-xs font-bold text-white hover:bg-black disabled:opacity-50">
            {scanning && <Loader2 size={12} className="animate-spin" />} Preview clean-up
          </button>
          <button data-testid="price-strip-apply-all-btn" disabled={busy || !ready} onClick={() => post("/price-strip/apply-all", (x) => `Prices moved out of ${x.venues} venue(s) in ${x.kits} kit(s)`)}
            className="rounded-sm bg-[#C9A847] px-3 py-1.5 text-xs font-bold text-white hover:bg-[#B8973A] disabled:opacity-50">Apply all ready ({ready})</button>
        </div>
      </div>
      <ul className="mt-2 max-h-56 overflow-y-auto">{kits.map((k, i) => <Row key={k.id} k={k} i={i} onView={setView} />)}</ul>
      <PriceStripPreview kitId={view} onClose={() => setView(null)} onApplied={load} />
    </section>
  );
};
